import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, FileCheck, DollarSign, Activity, Clock, AlertTriangle,
  CheckCircle2, XCircle, Search, ChevronLeft, ChevronRight, RefreshCw,
  BarChart3, TrendingUp, Eye, Ban, Loader2,
} from 'lucide-react';
import api, { auth, verifications } from '../lib/api';
import type { User, Verification } from '../lib/api';

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────
type AdminTab = 'overview' | 'verifications' | 'users' | 'revenue' | 'audit' | 'health';

interface DashboardStats {
  totalVerifications: number;
  pendingVerifications: number;
  verifiedCount: number;
  failedCount: number;
  totalUsers: number;
  totalRevenue: number;
  todayVerifications: number;
  avgConfidence: number;
}

interface AuditEntry {
  id: string;
  user_id: string | null;
  action: string;
  action_category: string;
  ip_address: string;
  request_method: string;
  request_path: string;
  response_status: number;
  response_time_ms: number;
  created_at: string;
}

interface HealthStatus {
  status: string;
  uptime: number;
  services: { database: string; payments: string };
  memory: { rss: string; heapUsed: string; heapTotal: string };
  environment: string;
  version: string;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Data states
  const [stats, setStats] = useState<DashboardStats>({
    totalVerifications: 0, pendingVerifications: 0, verifiedCount: 0, failedCount: 0,
    totalUsers: 0, totalRevenue: 0, todayVerifications: 0, avgConfidence: 0,
  });
  const [verificationList, setVerificationList] = useState<Verification[]>([]);
  const [verificationPage, setVerificationPage] = useState(1);
  const [verificationTotal, setVerificationTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([]);
  const [healthData, setHealthData] = useState<HealthStatus | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Auth check
  useEffect(() => {
    (async () => {
      try {
        const user = await auth.getUser();
        if (!user || user.role !== 'admin') {
          navigate('/login');
          return;
        }
        setCurrentUser(user);
        await loadDashboardData();
      } catch {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // ── Data Loading ─────────────────────────────
  const loadDashboardData = async () => {
    await Promise.allSettled([
      loadStats(),
      loadVerifications(),
      loadHealth(),
    ]);
  };

  const loadStats = async () => {
    try {
      const statsData = await verifications.getStats();
      setStats(prev => ({
        ...prev,
        totalVerifications: statsData.total,
        pendingVerifications: statsData.pending,
        verifiedCount: statsData.verified,
        failedCount: statsData.failed,
        avgConfidence: parseFloat(statsData.averageConfidence || '0'),
      }));
    } catch { /* stats unavailable */ }
  };

  const loadVerifications = async (page = 1, status = statusFilter) => {
    try {
      const data = await verifications.list({ page, limit: 20, status: status || undefined });
      setVerificationList(data.verifications);
      setVerificationTotal(data.pagination.total);
      setVerificationPage(page);
    } catch { /* verifications unavailable */ }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await api.request<AuditEntry[]>('/api/admin/audit-logs?limit=50');
      if (response.success && response.data) {
        setAuditLogs(response.data);
      }
    } catch { /* audit logs unavailable */ }
  };

  const loadHealth = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:3001')}/health`
      );
      const data = await response.json();
      setHealthData(data);
    } catch { /* health unavailable */ }
  };

  // Tab change handler
  useEffect(() => {
    if (activeTab === 'audit' && auditLogs.length === 0) {
      loadAuditLogs();
    }
    if (activeTab === 'health') {
      loadHealth();
    }
    if (activeTab === 'verifications') {
      loadVerifications();
    }
  }, [activeTab]);

  // Filter change
  useEffect(() => {
    if (activeTab === 'verifications') {
      loadVerifications(1, statusFilter);
    }
  }, [statusFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  // ── Stat Cards ───────────────────────────────
  const statCards = [
    { label: 'Total Verifications', value: stats.totalVerifications, icon: FileCheck, color: 'blue' },
    { label: 'Pending Review', value: stats.pendingVerifications, icon: Clock, color: 'yellow' },
    { label: 'Verified', value: stats.verifiedCount, icon: CheckCircle2, color: 'green' },
    { label: 'Failed', value: stats.failedCount, icon: XCircle, color: 'red' },
  ];

  const colorMap: Record<string, string> = {
    blue: 'bg-blue-500/20 text-blue-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    green: 'bg-green-500/20 text-green-400',
    red: 'bg-red-500/20 text-red-400',
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending: 'bg-yellow-900/30 text-yellow-400',
      processing: 'bg-blue-900/30 text-blue-400',
      verified: 'bg-green-900/30 text-green-400',
      failed: 'bg-red-900/30 text-red-400',
      expired: 'bg-gray-700 text-gray-400',
      cancelled: 'bg-gray-700 text-gray-400',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-700 text-gray-400'}`}>
        {status}
      </span>
    );
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-PH', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const formatUptime = (seconds: number) => {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${d}d ${h}h ${m}m`;
  };

  // ── Tabs ─────────────────────────────────────
  const tabs: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <BarChart3 className="w-4 h-4" /> },
    { key: 'verifications', label: 'Verifications', icon: <FileCheck className="w-4 h-4" /> },
    { key: 'users', label: 'Users', icon: <Users className="w-4 h-4" /> },
    { key: 'revenue', label: 'Revenue', icon: <DollarSign className="w-4 h-4" /> },
    { key: 'audit', label: 'Audit Log', icon: <img src="/pwa-192x192.png" alt="Agos" className="w-4 h-4 object-contain" /> },
    { key: 'health', label: 'Health', icon: <Activity className="w-4 h-4" /> },
  ];

  // ── Tab Content ──────────────────────────────
  const renderTab = () => {
    switch (activeTab) {
      // ── Overview ──
      case 'overview':
        return (
          <div>
            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {statCards.map(card => (
                <motion.div
                  key={card.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">{card.label}</span>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorMap[card.color]}`}>
                      <card.icon className="w-4 h-4" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{card.value.toLocaleString()}</p>
                </motion.div>
              ))}
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Avg confidence */}
              <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-5">
                <h3 className="text-white font-medium mb-3 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2 text-blue-400" />
                  Average Confidence Score
                </h3>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-white">
                    {(stats.avgConfidence * 100).toFixed(1)}%
                  </span>
                  <span className="text-gray-400 text-sm mb-1">across all verifications</span>
                </div>
                <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${stats.avgConfidence * 100}%` }}
                  />
                </div>
              </div>

              {/* Success rate */}
              <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-5">
                <h3 className="text-white font-medium mb-3 flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-2 text-green-400" />
                  Verification Success Rate
                </h3>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-white">
                    {stats.totalVerifications > 0
                      ? ((stats.verifiedCount / stats.totalVerifications) * 100).toFixed(1)
                      : '0.0'}%
                  </span>
                  <span className="text-gray-400 text-sm mb-1">
                    {stats.verifiedCount} of {stats.totalVerifications}
                  </span>
                </div>
                <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{
                      width: stats.totalVerifications > 0
                        ? `${(stats.verifiedCount / stats.totalVerifications) * 100}%`
                        : '0%',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      // ── Verifications ──
      case 'verifications':
        return (
          <div>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by ID or type..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                  aria-label="Search verifications"
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="bg-gray-800/50 border border-gray-700 rounded-lg text-white text-sm px-3 py-2 focus:border-blue-500 focus:outline-none"
                aria-label="Filter by status"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="verified">Verified</option>
                <option value="failed">Failed</option>
                <option value="expired">Expired</option>
              </select>
              <button
                type="button"
                onClick={() => loadVerifications(verificationPage)}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-gray-400 py-3 px-3 font-medium">ID</th>
                    <th className="text-left text-gray-400 py-3 px-3 font-medium">ID Type</th>
                    <th className="text-left text-gray-400 py-3 px-3 font-medium">Status</th>
                    <th className="text-left text-gray-400 py-3 px-3 font-medium">Confidence</th>
                    <th className="text-left text-gray-400 py-3 px-3 font-medium">Created</th>
                    <th className="text-left text-gray-400 py-3 px-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {verificationList.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-gray-500 py-8">
                        No verifications found
                      </td>
                    </tr>
                  ) : (
                    verificationList
                      .filter(v => !searchQuery ||
                        v.id.includes(searchQuery) ||
                        v.idType.includes(searchQuery.toLowerCase())
                      )
                      .map(v => (
                        <tr key={v.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                          <td className="py-3 px-3 font-mono text-gray-300 text-xs">
                            {v.id.slice(0, 8)}...
                          </td>
                          <td className="py-3 px-3 text-white">{v.idType}</td>
                          <td className="py-3 px-3">{statusBadge(v.status)}</td>
                          <td className="py-3 px-3 text-gray-300">
                            {v.confidenceScore != null
                              ? `${(v.confidenceScore * 100).toFixed(0)}%`
                              : '-'}
                          </td>
                          <td className="py-3 px-3 text-gray-400 text-xs">
                            {formatDate(v.createdAt)}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex gap-1">
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
                                title="View details"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              {v.status === 'pending' && (
                                <>
                                  <button
                                    type="button"
                                    className="p-1.5 rounded hover:bg-green-900/30 text-gray-400 hover:text-green-400"
                                    title="Approve"
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    className="p-1.5 rounded hover:bg-red-900/30 text-gray-400 hover:text-red-400"
                                    title="Reject"
                                  >
                                    <Ban className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <span className="text-gray-400 text-sm">
                {verificationTotal} total verifications
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => loadVerifications(verificationPage - 1)}
                  disabled={verificationPage <= 1}
                  className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white disabled:opacity-40"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="flex items-center text-gray-300 text-sm px-2">
                  Page {verificationPage}
                </span>
                <button
                  type="button"
                  onClick={() => loadVerifications(verificationPage + 1)}
                  disabled={verificationList.length < 20}
                  className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white disabled:opacity-40"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        );

      // ── Users ──
      case 'users':
        return (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white font-medium mb-2">User Management</h3>
            <p className="text-gray-400 text-sm mb-4">
              User search, suspend, and delete functionality.
              Requires admin API endpoints (admin/users) to be implemented.
            </p>
            <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4 max-w-md mx-auto text-left">
              <p className="text-gray-300 text-sm mb-2">Planned features:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>- Search users by email, name, or company</li>
                <li>- View user verification history</li>
                <li>- Suspend/unsuspend accounts</li>
                <li>- Delete accounts (GDPR/Data Privacy Act)</li>
                <li>- View user API usage</li>
              </ul>
            </div>
          </div>
        );

      // ── Revenue ──
      case 'revenue':
        return (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white font-medium mb-2">Revenue Dashboard</h3>
            <p className="text-gray-400 text-sm mb-4">
              Payment history and revenue analytics.
              Requires admin payment API endpoints to be implemented.
            </p>
            <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4 max-w-md mx-auto text-left">
              <p className="text-gray-300 text-sm mb-2">Planned features:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>- Daily/weekly/monthly revenue charts</li>
                <li>- Payment method breakdown (GCash, Maya, Card)</li>
                <li>- Refund tracking</li>
                <li>- Revenue by verification type (Basic vs Enhanced)</li>
                <li>- Export to CSV</li>
              </ul>
            </div>
          </div>
        );

      // ── Audit Log ──
      case 'audit':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-medium">Recent Audit Entries</h3>
              <button
                type="button"
                onClick={loadAuditLogs}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {auditLogs.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-3">
                  <img src="/pwa-192x192.png" alt="Agos Logo" className="w-10 h-10 object-contain opacity-50" />
                </div>
                <p className="text-gray-400 text-sm">
                  No audit logs available. Logs are recorded for all API requests.
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Requires /api/admin/audit-logs endpoint to be implemented.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {auditLogs.map(log => (
                  <div
                    key={log.id}
                    className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 flex items-center gap-3"
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      log.response_status < 400 ? 'bg-green-500' :
                      log.response_status < 500 ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-mono">
                          {log.request_method} {log.request_path}
                        </span>
                        <span className={`text-xs ${
                          log.response_status < 400 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {log.response_status}
                        </span>
                      </div>
                      <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                        <span>{log.ip_address}</span>
                        <span>{log.response_time_ms}ms</span>
                        <span>{formatDate(log.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      // ── Health ──
      case 'health':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-medium">System Health</h3>
              <button
                type="button"
                onClick={loadHealth}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {healthData ? (
              <div className="space-y-4">
                {/* Status badge */}
                <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm ${
                  healthData.status === 'healthy'
                    ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                    : 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30'
                }`}>
                  {healthData.status === 'healthy'
                    ? <CheckCircle2 className="w-4 h-4 mr-1.5" />
                    : <AlertTriangle className="w-4 h-4 mr-1.5" />
                  }
                  System {healthData.status}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Services */}
                  <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4">
                    <h4 className="text-gray-400 text-sm mb-3">Services</h4>
                    <div className="space-y-2">
                      {Object.entries(healthData.services).map(([name, status]) => (
                        <div key={name} className="flex items-center justify-between">
                          <span className="text-white text-sm capitalize">{name}</span>
                          <span className={`text-xs ${
                            status === 'connected' || status === 'paymongo'
                              ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Memory */}
                  <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4">
                    <h4 className="text-gray-400 text-sm mb-3">Memory</h4>
                    <div className="space-y-2">
                      {Object.entries(healthData.memory).map(([name, value]) => (
                        <div key={name} className="flex items-center justify-between">
                          <span className="text-white text-sm">{name}</span>
                          <span className="text-gray-300 text-sm">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4">
                    <h4 className="text-gray-400 text-sm mb-3">Server Info</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-white text-sm">Uptime</span>
                        <span className="text-gray-300 text-sm">{formatUptime(healthData.uptime)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white text-sm">Version</span>
                        <span className="text-gray-300 text-sm">{healthData.version}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white text-sm">Environment</span>
                        <span className="text-gray-300 text-sm">{healthData.environment}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                <p className="text-gray-400">Could not connect to health endpoint</p>
                <p className="text-gray-500 text-sm mt-1">Ensure the server is running</p>
              </div>
            )}
          </div>
        );
    }
  };

  // ── Main Layout ──────────────────────────────
  return (
    <div className="min-h-screen bg-slate-950 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-gray-400 text-sm mt-1">
              Logged in as {currentUser?.email} ({currentUser?.role})
            </p>
          </div>
          <button
            type="button"
            onClick={loadDashboardData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 text-sm"
          >
            <RefreshCw className="w-4 h-4" /> Refresh All
          </button>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
        >
          {renderTab()}
        </motion.div>
      </div>
    </div>
  );
}
